import { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const PUBLIC_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const UPLOAD_URL =
    (process.env.REACT_APP_EVENT_UPLOAD_URL || "").trim() ||
    `${PUBLIC_BASE}/event-upload.php`;

export default function AddEventDialog({ onSuccess }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        admin_key: "",
        name: "",
        url: "",
        start_at: "",
        timezone: "Africa/Nairobi",
        description: "",
    });
    const [file, setFile] = useState(null);

    const onChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.admin_key || !form.name || !form.url || !form.start_at) {
            toast.error("Admin key, title, Luma URL, and start time are required.");
            return;
        }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("admin_key", form.admin_key);
            fd.append("name", form.name.trim());
            fd.append("url", form.url.trim());
            fd.append("start_at", form.start_at.trim());
            fd.append("timezone", form.timezone.trim() || "Africa/Nairobi");
            fd.append("description", form.description.trim());
            if (file) {
                fd.append("cover", file);
            }

            const res = await fetch(UPLOAD_URL, {
                method: "POST",
                body: fd,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) {
                throw new Error(data.error || `Server error (${res.status})`);
            }
            toast.success("Event saved. Images are under /eventimages/ on your host.");
            setOpen(false);
            setForm({
                admin_key: "",
                name: "",
                url: "",
                start_at: "",
                timezone: "Africa/Nairobi",
                description: "",
            });
            setFile(null);
            onSuccess?.();
        } catch (err) {
            toast.error(
                err.message ||
                    "Upload failed. On localhost, PHP is not running — deploy to cheppar.co.ke with event-upload.php configured.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="add-event-open"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-black/15 bg-white text-sm font-medium text-zinc-900 shadow-sm hover:bg-black/[0.03] transition-colors"
            >
                <Plus className="h-4 w-4" />
                Add event
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto rounded-2xl border-black/10">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-bold">
                            Add event
                        </DialogTitle>
                        <DialogDescription className="text-zinc-600 leading-relaxed">
                            Saves the cover image to{" "}
                            <strong className="text-zinc-800">
                                /eventimages/
                            </strong>{" "}
                            on your server and appends{" "}
                            <code className="text-xs bg-zinc-100 px-1 rounded">
                                manual-events.json
                            </code>
                            . Requires{" "}
                            <code className="text-xs bg-zinc-100 px-1 rounded">
                                event-upload.php
                            </code>{" "}
                            next to your site root (included in{" "}
                            <code className="text-xs">public/</code>). Set{" "}
                            <code className="text-xs">ADMIN_KEY</code> inside
                            that file on the server.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={onSubmit} className="space-y-4 mt-2">
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="admin_key"
                                className="text-xs font-mono uppercase tracking-[0.12em] text-zinc-500"
                            >
                                Admin password
                            </Label>
                            <Input
                                id="admin_key"
                                name="admin_key"
                                type="password"
                                autoComplete="off"
                                value={form.admin_key}
                                onChange={onChange}
                                placeholder="Matches ADMIN_KEY in event-upload.php"
                                className="rounded-xl border-black/10"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-name">Event title</Label>
                            <Input
                                id="ev-name"
                                name="name"
                                value={form.name}
                                onChange={onChange}
                                placeholder="Global Pizza Party"
                                className="rounded-xl border-black/10"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-url">Luma event URL</Label>
                            <Input
                                id="ev-url"
                                name="url"
                                type="url"
                                value={form.url}
                                onChange={onChange}
                                placeholder="https://lu.ma/..."
                                className="rounded-xl border-black/10"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-start">
                                Start time (ISO 8601)
                            </Label>
                            <Input
                                id="ev-start"
                                name="start_at"
                                value={form.start_at}
                                onChange={onChange}
                                placeholder="2026-05-22T17:00:00+03:00"
                                className="rounded-xl border-black/10 font-mono text-sm"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-tz">Timezone</Label>
                            <Input
                                id="ev-tz"
                                name="timezone"
                                value={form.timezone}
                                onChange={onChange}
                                placeholder="Africa/Nairobi"
                                className="rounded-xl border-black/10"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-desc">Description (optional)</Label>
                            <Textarea
                                id="ev-desc"
                                name="description"
                                value={form.description}
                                onChange={onChange}
                                rows={3}
                                className="rounded-xl border-black/10 resize-none"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ev-cover">Cover image (optional)</Label>
                            <Input
                                id="ev-cover"
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={(e) =>
                                    setFile(e.target.files?.[0] || null)
                                }
                                className="rounded-xl border-black/10 cursor-pointer"
                            />
                            <p className="text-[11px] text-zinc-500">
                                Stored as{" "}
                                <code className="bg-zinc-100 px-1 rounded">
                                    cheppar.co.ke/eventimages/&lt;file&gt;
                                </code>
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-full bg-zinc-950 text-white text-sm font-medium hover:bg-[#E54D2E] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    <Plus className="h-4 w-4" />
                                    Save event
                                </>
                            )}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
